/**
 * Helpers for reporting oversized / failed multipart uploads.
 *
 * An upload that exceeds a size limit almost never comes back as a tidy JSON
 * error, so `err.response.data.message` on its own is not enough:
 *
 *  - a reverse proxy over `client_max_body_size` answers 413 with an HTML body;
 *  - PHP over `post_max_size` (110M, see the API's docker/php/uploads.ini)
 *    discards the whole request body, so Laravel validates an empty request and
 *    replies 422 with "field is required" for fields the user did fill in;
 *  - PHP over `upload_max_filesize` (100M) drops just the file, so the file
 *    field reads as missing or "failed to upload";
 *  - the browser may abort before any response arrives at all.
 */

// Per-image cap. The API only validates `images.*.file => nullable|image` with
// no `max:` rule, so this is a client-side guard: keep it in sync if a server
// rule is added.
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024

// Whole-request cap, kept well under the API's 110M post_max_size so a request
// that passes this check is never silently discarded by PHP.
export const MAX_TOTAL_UPLOAD_BYTES = 50 * 1024 * 1024

export const formatBytes = (bytes) => {
  if (!bytes) return '0 MB'
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Turn an axios error from a multipart upload into a message worth showing.
 *
 * @param {Error} err axios error
 * @param {object} [opts]
 * @param {string} [opts.fallback] message when nothing more specific is known
 * @param {number} [opts.uploadedBytes] size of the payload that was sent
 * @param {string[]} [opts.sentFields] fields sent with a non-empty value; a
 *   `required` error on any of them means the body never arrived intact
 * @returns {string}
 */
export const uploadErrorMessage = (err, opts = {}) => {
  const { fallback = 'Something went wrong. Please try again.', uploadedBytes, sentFields = [] } = opts
  const { response } = err
  const sizeSuffix = uploadedBytes ? ` (${formatBytes(uploadedBytes)} sent)` : ''

  if (!response) {
    return `The upload did not reach the server${sizeSuffix}. The files may be too large, or the connection dropped — try again with smaller images.`
  }

  if (response.status === 413) {
    return `The upload is too large for the server to accept${sizeSuffix}. Remove or shrink some images and try again.`
  }

  const errors = response.data?.errors || {}

  if (response.status === 422) {
    // The server says fields are missing that we know were sent — the request
    // body was truncated, which means the payload was over the limit.
    const droppedBody = sentFields.some((field) =>
      (errors[field] || []).some((msg) => /required/i.test(msg))
    )
    if (droppedBody) {
      return `The upload was too large and the server discarded it${sizeSuffix}. Remove or shrink some images and try again.`
    }

    const fileError = Object.keys(errors)
      .filter((key) => /\.file$/.test(key))
      .flatMap((key) => errors[key])
      .find((msg) => /greater than|failed to upload|must be an image/i.test(msg))
    if (fileError) return fileError
  }

  const message = response.data?.message
  // Guard against HTML error pages being rendered as the message.
  if (typeof message === 'string' && message && !/^\s*</.test(message)) return message

  return fallback
}
